import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Shield, AlertTriangle, CheckCircle, Clock, TrendingUp, Ban, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const severityColor = {
  CRITICAL: 'bg-red-500/10 text-red-400 border-red-500/20',
  HIGH:     'bg-orange-500/10 text-orange-400 border-orange-500/20',
  MEDIUM:   'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  LOW:      'bg-green-500/10 text-green-400 border-green-500/20',
};

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 flex items-center gap-4">
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="h-5 w-5" strokeWidth={1.5} />
      </div>
      <div>
        <p className="text-muted-foreground text-xs uppercase tracking-wide">{label}</p>
        <p className="font-outfit text-2xl font-semibold">{value ?? '—'}</p>
      </div>
    </div>
  );
}

export default function FraudDashboardPage() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/fraud/stats`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStats(await res.json());
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchStats(); }, []);

  return (
    <div className="space-y-6" data-testid="fraud-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-semibold flex items-center gap-2">
            <Shield className="h-6 w-6 text-accent" strokeWidth={1.5} />
            Fraud Dashboard
          </h1>
          <p className="text-muted-foreground text-sm">Overview of fraud detection and case management</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStats} className="gap-2">
          <RefreshCw className="h-4 w-4" strokeWidth={1.5} /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-accent border-t-transparent rounded-full" />
        </div>
      ) : stats ? (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Open Cases"    value={stats.open_cases}     icon={Clock}       color="bg-yellow-500/10 text-yellow-400" />
            <StatCard label="Total Cases"   value={stats.total_cases}    icon={Shield}      color="bg-blue-500/10 text-blue-400"   />
            <StatCard label="False Positive Rate" value={`${stats.false_positive_rate}%`} icon={CheckCircle} color="bg-green-500/10 text-green-400" />
            <StatCard label="Avg Review"    value={stats.avg_review_time_hrs != null ? `${stats.avg_review_time_hrs}h` : '—'} icon={TrendingUp} color="bg-purple-500/10 text-purple-400" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* By severity */}
            <div className="bg-card border border-border rounded-xl p-5 space-y-3">
              <h2 className="font-outfit font-semibold text-sm uppercase tracking-wide text-muted-foreground">Cases by Severity</h2>
              {Object.entries(stats.cases_by_severity || {}).map(([sev, cnt]) => (
                <div key={sev} className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${severityColor[sev] || 'bg-muted text-muted-foreground'}`}>{sev}</span>
                  <span className="font-semibold">{cnt}</span>
                </div>
              ))}
            </div>

            {/* Top offending IPs */}
            <div className="bg-card border border-border rounded-xl p-5 space-y-3">
              <h2 className="font-outfit font-semibold text-sm uppercase tracking-wide text-muted-foreground flex items-center justify-between">
                Top Offending IPs
                <Link to="/admin/fraud/blocklist" className="text-accent text-xs hover:underline">Manage →</Link>
              </h2>
              {(stats.top_offending_ips || []).slice(0, 8).map((item) => (
                <div key={item.ip} className="flex items-center justify-between text-sm">
                  <span className="font-mono text-muted-foreground">{item.ip}</span>
                  <span className="font-semibold">{item.count} cases</span>
                </div>
              ))}
              {(stats.top_offending_ips || []).length === 0 && (
                <p className="text-muted-foreground text-sm">No data yet.</p>
              )}
            </div>
          </div>

          {/* Cases last 7d */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <h2 className="font-outfit font-semibold text-sm uppercase tracking-wide text-muted-foreground">Cases Last 7 Days</h2>
            <div className="flex items-end gap-2 h-20">
              {(stats.cases_last_7d || []).map((d) => {
                const max = Math.max(...(stats.cases_last_7d || []).map((x) => x.count), 1);
                const pct = Math.round((d.count / max) * 100);
                return (
                  <div key={d.date} className="flex flex-col items-center gap-1 flex-1">
                    <span className="text-xs text-muted-foreground">{d.count}</span>
                    <div className="w-full bg-accent/70 rounded-t" style={{ height: `${pct}%`, minHeight: d.count > 0 ? 4 : 0 }} />
                    <span className="text-xs text-muted-foreground">{d.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick links */}
          <div className="flex gap-3">
            <Link to="/admin/fraud/cases?status=pending_review">
              <Button variant="outline" size="sm" className="gap-2">
                <AlertTriangle className="h-4 w-4" strokeWidth={1.5} /> Review Queue ({stats.open_cases})
              </Button>
            </Link>
            <Link to="/admin/fraud/blocklist">
              <Button variant="outline" size="sm" className="gap-2">
                <Ban className="h-4 w-4" strokeWidth={1.5} /> IP Controls
              </Button>
            </Link>
          </div>
        </>
      ) : (
        <p className="text-muted-foreground">No fraud data available.</p>
      )}
    </div>
  );
}
